import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Lock, LogOut, Loader2, FileText, Receipt, MapPin, Eye, Scale, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, money, downloadBlob, applyBrand } from "@/lib/api";
import { PortalBrand } from "@/pages/PortalLogin";
import { RepeatOffer } from "@/components/RepeatOffer";

const Card = ({ title, icon: Icon, children, testId }) => (
  <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8" data-testid={testId}>
    <h2 className="flex items-center gap-2 font-display text-lg font-bold mb-5"><Icon className="h-5 w-5 text-emerald-600" />{title}</h2>{children}
  </section>
);

const Studies = ({ studies }) => {
  const [busy, setBusy] = useState("");
  const dl = async (s) => {
    setBusy(s.id);
    try { await downloadBlob(`/portal/studies/${s.id}/docx`, `${(s.name || "study").normalize("NFKD").replace(/[^\w]+/g, "-")}.docx`); }
    catch { toast.error("Download failed"); } finally { setBusy(""); }
  };
  if (!studies.length) return <p className="text-sm text-slate-500" data-testid="portal-no-studies">No studies yet.</p>;
  return (
    <div className="divide-y divide-slate-200">
      {studies.map((s) => (
        <div key={s.id} className="py-4 flex flex-wrap items-center justify-between gap-4" data-testid={`portal-study-${s.id}`}>
          <div>
            <p className="font-semibold">{s.name}</p>
            <p className="text-xs text-slate-500 flex items-center gap-1">{s.address && <MapPin className="h-3 w-3" />}{[s.address, s.industry, s.verdict && `${s.verdict} (${s.score}/100)`].filter(Boolean).join(" · ")}</p>
          </div>
          {s.unlocked ? (
            <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" className="rounded-full border-slate-300 bg-white text-slate-900"><Link to={`/portal/studies/${s.id}`} data-testid={`portal-view-${s.id}`}><Eye className="h-4 w-4 mr-2" />View online</Link></Button>
            <Button data-testid={`portal-download-${s.id}`} disabled={busy === s.id} onClick={() => dl(s)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy === s.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download (Word)
            </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" />{s.invoice ? "Unlocks after payment" : "Invoice coming soon"}</span>
              {s.preview && <Button asChild variant="outline" className="rounded-full border-amber-300 bg-amber-50 text-amber-900"><Link to={`/portal/studies/${s.id}/preview`} data-testid={`portal-preview-${s.id}`}><Eye className="h-4 w-4 mr-2" />Preview draft</Link></Button>}
              {s.invoice && <Button asChild variant="outline" className="rounded-full border-slate-300 bg-white text-slate-900"><a href={`/pay/${s.invoice.token}`} data-testid={`portal-pay-${s.id}`}>Pay {s.invoice.number}</a></Button>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Comparisons = ({ items }) => (
  <div className="divide-y divide-slate-200">
    {items.map((c) => (
      <div key={c.id} className="py-4 flex flex-wrap items-center justify-between gap-4" data-testid={`portal-comparison-${c.id}`}>
        <div>
          <p className="font-semibold">{c.name}</p>
          <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{c.address} · {c.brands.join(" vs ")}</p>
          {c.winner && <p className="text-xs text-emerald-700 flex items-center gap-1 mt-1"><Trophy className="h-3 w-3" />Recommended: {c.winner}</p>}
        </div>
        <Button asChild variant="outline" className="rounded-full border-slate-300 bg-white text-slate-900"><Link to={`/portal/compare/${c.id}`} data-testid={`portal-comparison-view-${c.id}`}><Eye className="h-4 w-4 mr-2" />View</Link></Button>
      </div>
    ))}
  </div>
);

const Invoices = ({ invoices }) => {
  const receipt = (i) => downloadBlob(`/portal/invoices/${i.id}/receipt`, `receipt-${i.number}.pdf`).catch(() => toast.error("Download failed"));
  if (!invoices.length) return <p className="text-sm text-slate-500">No invoices yet.</p>;
  return (
    <div className="divide-y divide-slate-200">
      {invoices.map((i) => (
        <div key={i.id} className="py-4 flex flex-wrap items-center justify-between gap-4" data-testid={`portal-invoice-${i.id}`}>
          <div><p className="font-semibold num">{i.number} <span className={`ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${i.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{i.status}</span></p>
            <p className="text-xs text-slate-500">{i.description} · {money(i.amount, i.currency)}</p></div>
          {i.status === "paid"
            ? <Button variant="outline" data-testid={`portal-receipt-${i.id}`} onClick={() => receipt(i)} className="rounded-full border-slate-300 bg-white text-slate-900"><Receipt className="h-4 w-4 mr-2" />Receipt (PDF)</Button>
            : <Button asChild className="rounded-full bg-slate-900 text-white hover:bg-slate-800"><a href={`/pay/${i.token}`} data-testid={`portal-invoice-pay-${i.id}`}>View & pay</a></Button>}
        </div>
      ))}
    </div>
  );
};

const PasswordCard = ({ me }) => {
  const [pw, setPw] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/portal/password", { password: pw }); setPw(""); toast.success("Password saved — you can now sign in with your email and password"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card title={me.has_password ? "Change password" : "Set a password"} icon={Lock} testId="portal-password-card">
      <form onSubmit={save} className="flex flex-wrap gap-3">
        <Input data-testid="portal-new-password" type="password" minLength={8} required placeholder="New password (8+ characters)" value={pw} onChange={(e) => setPw(e.target.value)} className="flex-1 min-w-[220px] h-11 bg-white border-slate-300 text-slate-900" />
        <Button data-testid="portal-save-password" className="h-11 rounded-full bg-slate-900 text-white hover:bg-slate-800">Save</Button>
      </form>
    </Card>
  );
};

export default function Portal() {
  const [me, setMe] = useState(null);
  const [data, setData] = useState({ studies: [], invoices: [], comparisons: [] });
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    api.get("/portal/me").then((r) => { setMe(r.data); applyBrand(r.data.brand); }).catch(() => setMe(false));
    api.get("/portal/offer").then((r) => setOffer(r.data)).catch(() => {});
    Promise.all([api.get("/portal/studies"), api.get("/portal/invoices"), api.get("/portal/comparisons")]).then(([s, i, c]) => setData({ studies: s.data, invoices: i.data, comparisons: c.data })).catch(() => {});
  }, []);
  if (me === false) return <Navigate to="/portal/login" replace />;
  if (!me) return <div className="min-h-screen bg-slate-100 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  const logout = () => api.post("/portal/logout").finally(() => setMe(false));

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="portal-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <PortalBrand branding={me} compact />
          <button data-testid="portal-logout" onClick={logout} className="flex items-center gap-2 text-sm text-slate-500 hover:text-rose-600"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <div><p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-1">Client portal</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="portal-welcome">Welcome, {me.name}</h1>
          {me.company && <p className="text-slate-500">{me.company}</p>}</div>
        <RepeatOffer offer={offer} />
        <Card title="Your feasibility studies" icon={FileText} testId="portal-studies"><Studies studies={data.studies} /></Card>
        {data.comparisons.length > 0 && <Card title="Franchise comparisons" icon={Scale} testId="portal-comparisons"><Comparisons items={data.comparisons} /></Card>}
        <Card title="Invoices & receipts" icon={Receipt} testId="portal-invoices"><Invoices invoices={data.invoices} /></Card>
        <PasswordCard me={me} />
      </main>
    </div>
  );
}
